import React, { useState, useEffect, useRef, useMemo } from 'react';
import API_BASE from '../config';
import { getCached, setCached } from '../lib/listCache';
import usePullToRefresh from '../lib/usePullToRefresh';
import PullIndicator from './PullIndicator';
import ContactPicker from './ContactPicker';
import { useLineContacts, isNumberSaved, findContactByNumber, savedContactName } from '../lib/lineContacts';
import { resolveRemoteLang } from '../lib/translateSettings';
import AddToExistingContact from './AddToExistingContact';
import { formatNumber } from '../lib/phoneFormat';
import { isNearBottom, threadSnapshot, shouldAutoScroll } from './messagesScroll';
import {
  TRANSLATE_LANGUAGES,
  applyLineTranslateFromServer,
  loadTranslateSettings,
  resolveSmsThreadMode,
  saveSmsThreadMode,
} from '../lib/translateSettings';
import { formatInTimeZone, useDisplayTimeZone } from '../lib/displayTimezone';
import { resolveMediaRenderType } from '../lib/mediaRender';


// Turn a message body into React nodes, upgrading http(s) URLs into real
// <a> links. Non-URL runs stay plain text, so we never rely on
// dangerouslySetInnerHTML. The enclosing bubble keeps `white-space:
// pre-wrap` (see renderBubbleBody / .msg-bubble) so \n line breaks survive.
function linkifyText(text) {
  if (!text) return text;
  const urlRe = /(https?:\/\/[^\s]+)/g;
  const nodes = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      React.createElement(
        'a',
        { key: match.index, href: match[0], target: '_blank', rel: 'noopener noreferrer' },
        match[0]
      )
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length ? nodes : text;
}



// Format phone for display: +18445551234 → (844) 555-1234
function displayNumber(num) {
  if (!num) return '';
  const d = num.replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return num;
}

// SignalWire media URLs require HTTP Basic Auth with the carrier
// project/token to fetch the raw bytes -- a plain <img src="..."> against
// them always 401s (verified directly against a real inbound MMS). Fetch
// through our own authenticated proxy instead and render the result as a
// blob: URL, the standard pattern for an image behind a bearer-token API.
function MediaAttachment({ url, sid, authToken }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mime, setMime] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    let revoke = null;
    let cancelled = false;
    setBlobUrl(null);
    setFailed(false);
    setMime('');
    setName('');
    const mediaId = (url || '').split('/').filter(Boolean).pop();
    if (!sid || !mediaId) { setFailed(true); return undefined; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/messages/${encodeURIComponent(sid)}/media/${encodeURIComponent(mediaId)}`, {
          headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
        });
        if (!res.ok) throw new Error(`media fetch ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const objUrl = URL.createObjectURL(blob);
        revoke = objUrl;
        setBlobUrl(objUrl);
        setMime(blob.type || '');
        setName(blob.name || '');
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [url, sid, authToken]);

  if (failed) {
    return (
      <div style={{ fontSize: 13, opacity: 0.7, padding: '6px 8px', border: '1px dashed var(--truva-border)', borderRadius: 8 }}>
        📎 Attachment unavailable
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div style={{ width: 120, height: 120, borderRadius: 8, background: 'var(--truva-border)', opacity: 0.4 }} />
    );
  }
  const openInNewTab = () => window.open(blobUrl, '_blank', 'noopener,noreferrer');
  // blob.type is forwarded from the upstream Content-Type by the backend
  // media proxy, so it reliably tells us how to render this attachment.
  const renderType = resolveMediaRenderType(mime);
  const handleError = () => setFailed(true);

  if (renderType === 'image') {
    return (
      <img
        src={blobUrl}
        alt="attachment"
        style={{ maxWidth: 160, maxHeight: 160, borderRadius: 8, display: 'block', cursor: 'pointer' }}
        onClick={openInNewTab}
        onError={handleError}
      />
    );
  }
  if (renderType === 'video') {
    return (
      <video
        src={blobUrl}
        controls
        preload="metadata"
        style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, display: 'block' }}
        onError={handleError}
      />
    );
  }
  if (renderType === 'audio') {
    return (
      <audio
        src={blobUrl}
        controls
        style={{ maxWidth: 220, display: 'block' }}
        onError={handleError}
      />
    );
  }
  // PDF, vCard, octet-stream, unknown/empty: a compact download link, not an <img>.
  const label = name || (mime ? mime.split('/').pop() : '') || 'attachment';
  return (
    <div
      onClick={openInNewTab}
      style={{ fontSize: 14, padding: '8px 10px', border: '1px solid var(--truva-border)', borderRadius: 8, cursor: 'pointer', display: 'inline-block', opacity: 0.85 }}
    >
      📎 {label} — tap to open
    </div>
  );
}

async function translateTextApi(authToken, { text, targetLang, sourceLang, domain }) {
  const res = await fetch(`${API_BASE}/api/translate/text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      'ngrok-skip-browser-warning': 'true',
    },
    body: JSON.stringify({
      text,
      target_lang: targetLang,
      source_lang: sourceLang || undefined,
      domain: domain || 'personal',
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.error) {
    throw new Error(data.error || `Translate failed (${res.status})`);
  }
  return data;
}

export default function Messages({ selectedNumber, authToken, composeTo, setComposeTo, setUnreadCount, setPrefillContactNumber, setTab, handleCall, setNumber }) {
  const displayTz = useDisplayTimeZone();
  const [savedContacts, reloadSavedContacts] = useLineContacts(selectedNumber, authToken);
  const [activeContact, setActiveContact] = useState(null); // null = conversation list
  const [toNumber, setToNumber] = useState('');
  const [msgText, setMsgText] = useState('');
  const [messages, setMessages] = useState(() => getCached('messages', selectedNumber) || []);
  const [messagesLoaded, setMessagesLoaded] = useState(() => getCached('messages', selectedNumber) != null);
  const pollRef = useRef(null);
  const ptr = usePullToRefresh(() => (pollRef.current ? pollRef.current() : null));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [translateSettings, setTranslateSettings] = useState(() => loadTranslateSettings(selectedNumber));
  // Remote language actually used for this thread's translation: the saved
  // contact's translate_lang > the per-line/global setting (mirrors
  // resolveRemoteLang in lib). Read-only -- never writes back, so a manual
  // preference is never silently overridden.
  const threadRemoteLang = resolveRemoteLang(
    (findContactByNumber(savedContacts, activeContact) || {}).translate_lang || '',
    '',
    translateSettings.remoteLang,
  );
  const [threadMode, setThreadMode] = useState('ask');
  const [showOriginal, setShowOriginal] = useState({}); // sid -> bool
  const [translatingSids, setTranslatingSids] = useState({});
  const [composeTranslating, setComposeTranslating] = useState(false);
  // Pending "Ask" confirmation: { formatted, text, translated, targetLang }
  const [translateConfirm, setTranslateConfirm] = useState(null);
  const fileInputRef = useRef(null);
  // The element that actually scrolls the thread (not a sentinel + scrollIntoView,
  // which also scrolled ancestor containers).
  const threadScrollRef = useRef(null);
  // Last-seen newest message key / count for the *active* thread, so a poll
  // that returns identical content never moves the view.
  const threadSnapshotRef = useRef({ threadId: null, key: '', count: 0 });
  // Live "is the user at the bottom" flag, read from scroll events.
  //
  // This ref — not a measurement inside the render effect — is the autoscroll
  // input. The effect runs AFTER React has committed the new bubble, so
  // isNearBottom(el) there measures the grown content: one tall message pushes
  // the distance-to-bottom past NEAR_BOTTOM_PX and a reader parked at the
  // bottom gets misread as "scrolled up". The ref still holds the position
  // from BEFORE that commit, which is the position the decision is about.
  // onScroll maintains it, and scrollThreadToBottom / an own send force it true.
  const nearBottomRef = useRef(true);
  const forceScrollRef = useRef(false); // set right before an own send
  const [showNewPill, setShowNewPill] = useState(false);
  const prevInboundCountRef = useRef(0);
  const translateInflight = useRef(new Set());

  // Refresh settings when opening Messages / switching Line (Settings may have changed)
  useEffect(() => {
    setTranslateSettings(loadTranslateSettings(selectedNumber));
    if (!authToken || !selectedNumber) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(
          `${API_BASE}/api/agent/line-settings?number=${encodeURIComponent(selectedNumber)}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (!r.ok || cancelled) return;
        const d = await r.json();
        applyLineTranslateFromServer(selectedNumber, d);
        if (!cancelled) setTranslateSettings(loadTranslateSettings(selectedNumber));
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [activeContact, selectedNumber, authToken]);

  useEffect(() => {
    if (!activeContact) return;
    // Opening/switching a thread starts parked at the newest message; the
    // previous thread's scroll position must not leak into this decision.
    nearBottomRef.current = true;
    if (activeContact === '__new__') {
      setThreadMode(translateSettings.smsMode || 'ask');
      return;
    }
    setThreadMode(resolveSmsThreadMode(activeContact, translateSettings.smsMode));
  }, [activeContact, translateSettings.smsMode]);

  // If parent requests composing to a specific contact, open compose view
  useEffect(() => {
    if (composeTo) {
      setToNumber(composeTo);
      setActiveContact('__new__');
      if (typeof setComposeTo === 'function') setComposeTo(null);
    }
  }, [composeTo, setComposeTo]);

  // Play a short chime for new messages
  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      const now = ctx.currentTime;
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.setValueAtTime(1100, now + 0.1);
      osc.frequency.setValueAtTime(880, now + 0.2);
      gain.gain.setValueAtTime(0.25, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (e) { /* ignore */ }
  };

  // Poll messages for the selected line — mirrors CallLog.js's `?line=` param
  // so switching the header's line dropdown scopes Messages the same way it
  // already scopes Recents.
  useEffect(() => {
    const cachedForLine = getCached('messages', selectedNumber);
    setMessages(cachedForLine || []);
    setMessagesLoaded(cachedForLine != null);
    const poll = async () => {
      try {
        const qs = selectedNumber ? `?line=${encodeURIComponent(selectedNumber)}` : '';
        const res = await fetch(`${API_BASE}/messages${qs}`, { headers: { Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' } });
        if (res.ok) {
          const data = await res.json();
          const inboundTotal = data.filter(m => m.direction === 'inbound').length;
          if (prevInboundCountRef.current > 0 && inboundTotal > prevInboundCountRef.current) {
            playChime();
            if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            const newest = [...data].filter(m => m.direction === 'inbound').pop();
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && newest) {
              new Notification('New Message', {
                body: `From ${newest.from}: ${newest.body?.slice(0, 80) || ''}`,
              });
            }
          }
          prevInboundCountRef.current = inboundTotal;
          setMessages(data);
          setCached('messages', selectedNumber, data);
        }
        setMessagesLoaded(true);
      } catch (e) { /* ignore */ }
    };
    pollRef.current = poll;
    poll();
    const id = setInterval(poll, 4000);
    return () => { clearInterval(id); pollRef.current = null; };
  }, [authToken, selectedNumber]);

  const conversations = useMemo(() => {
    const map = {};
    messages.forEach((m) => {
      const contact = m.direction === 'outbound' ? m.to : m.from;
      if (!contact) return;
      if (!map[contact]) map[contact] = [];
      map[contact].push(m);
    });
    return Object.entries(map)
      .map(([contact, msgs]) => {
        // API returns newest-first, but optimistic sends append — always pick
        // max timestamp so the list preview is the most recent SMS.
        let lastMessage = null;
        for (const m of msgs) {
          const ts = m?.timestamp || '';
          if (!lastMessage || ts > (lastMessage.timestamp || '')) lastMessage = m;
        }
        return {
          contact,
          messages: msgs,
          lastMessage,
          unread: msgs.filter((m) => m.direction === 'inbound' && !m.is_read).length,
        };
      })
      .sort((a, b) => {
        const ta = a.lastMessage?.timestamp || '';
        const tb = b.lastMessage?.timestamp || '';
        return tb.localeCompare(ta);
      });
  }, [messages]);

  useEffect(() => {
    if (typeof setUnreadCount === 'function') {
      try {
        const totalUnread = conversations.reduce((s, c) => s + (c.unread || 0), 0);
        setUnreadCount(totalUnread);
      } catch (e) { /* ignore */ }
    }
  }, [conversations, setUnreadCount]);

  const threadMessages = useMemo(() => {
    if (!activeContact) return [];
    return messages
      .filter((m) => {
        const contact = m.direction === 'outbound' ? m.to : m.from;
        return contact === activeContact;
      })
      // Oldest at top, newest at bottom (standard SMS).
      .sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
  }, [messages, activeContact]);

  // Jump the thread container — never scrollIntoView(), which also scrolls
  // the page/ancestor panes.
  const scrollThreadToBottom = (smooth = false) => {
    const el = threadScrollRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    } catch (e) {
      el.scrollTop = el.scrollHeight;
    }
    nearBottomRef.current = true;
  };

  const threadSnapshotNow = threadSnapshot(threadMessages);

  // Autoscroll policy (see ./messagesScroll): content identity, not array
  // identity. A 4s poll that returns the same messages produces a new
  // threadMessages array — and must not move the view at all.
  useEffect(() => {
    if (!activeContact || activeContact === '__new__') {
      threadSnapshotRef.current = { threadId: activeContact || null, key: '', count: 0 };
      return;
    }
    const prev = threadSnapshotRef.current;
    const sameThread = prev.threadId === activeContact;
    const force = forceScrollRef.current;
    const { scroll, showPill } = shouldAutoScroll({
      threadId: activeContact,
      // A switch means "never seen this thread" → open at the bottom.
      lastSeenKey: sameThread ? prev.key : '',
      lastSeenCount: sameThread ? prev.count : 0,
      newestKey: threadSnapshotNow.key,
      count: threadSnapshotNow.count,
      nearBottom: nearBottomRef.current,
      force,
    });
    threadSnapshotRef.current = {
      threadId: activeContact,
      key: threadSnapshotNow.key,
      count: threadSnapshotNow.count,
    };
    forceScrollRef.current = false;
    if (scroll) {
      scrollThreadToBottom(force && sameThread);
      setShowNewPill(false);
    } else if (showPill) {
      setShowNewPill(true);
    }
    // Deliberately keyed on the content snapshot, not the array identity.
  }, [activeContact, threadSnapshotNow.key, threadSnapshotNow.count]);

  const handleThreadScroll = () => {
    const near = isNearBottom(threadScrollRef.current);
    nearBottomRef.current = near;
    if (near && showNewPill) setShowNewPill(false);
  };

  const scrollToNewest = () => {
    scrollThreadToBottom(true);
    setShowNewPill(false);
  };

  const persistTranslation = async (sid, bodyTranslated, detectedLang) => {
    try {
      await fetch(`${API_BASE}/messages/${encodeURIComponent(sid)}/translation`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify({
          body_translated: bodyTranslated,
          detected_lang: detectedLang,
        }),
      });
    } catch (_) { /* ignore */ }
  };

  const translateInbound = async (msg) => {
    if (!msg?.sid || !msg.body || translateInflight.current.has(msg.sid)) return;
    const targetLang = translateSettings.userLang || 'en';
    // Re-translate when a prior run targeted the wrong language (e.g. Spanish).
    if (msg.body_translated && msg._tx_target === targetLang) return;
    translateInflight.current.add(msg.sid);
    setTranslatingSids((prev) => ({ ...prev, [msg.sid]: true }));
    try {
      // Inbound: other party speaks remoteLang → translate into your userLang.
      // Pass sourceLang so accent-free Vietnamese is not mis-detected as Spanish.
      const data = await translateTextApi(authToken, {
        text: msg.body,
        targetLang,
        sourceLang: threadRemoteLang || undefined,
        domain: translateSettings.domain,
      });
      setMessages((prev) => prev.map((m) => (
        m.sid === msg.sid
          ? {
              ...m,
              body_translated: data.translated,
              detected_lang: data.detected_lang,
              _tx_target: data.target_lang || targetLang,
            }
          : m
      )));
      await persistTranslation(msg.sid, data.translated, data.detected_lang);
    } catch (e) {
      console.warn('[SMS-TRANSLATE] inbound failed', e);
    } finally {
      translateInflight.current.delete(msg.sid);
      setTranslatingSids((prev) => {
        const next = { ...prev };
        delete next[msg.sid];
        return next;
      });
    }
  };

  // Auto-translate inbound when thread mode is "always"
  useEffect(() => {
    if (!activeContact || activeContact === '__new__') return;
    if (threadMode !== 'always') return;
    const targetLang = translateSettings.userLang || 'en';
    threadMessages
      .filter((m) => (
        m.direction === 'inbound'
        && m.body
        && (!m.body_translated || m._tx_target !== targetLang)
      ))
      .forEach((m) => { translateInbound(m); });
  }, [threadMessages, threadMode, activeContact, translateSettings.userLang, threadRemoteLang]);

  const setThreadTranslateMode = (mode) => {
    if (!activeContact || activeContact === '__new__') return;
    saveSmsThreadMode(activeContact, mode);
    setThreadMode(mode);
  };

  // Actually POST to /sms. translateMeta (when set) carries the already-
  // translated body plus the language pair so the backend records both
  // body (translated, what's actually sent) and body_original.
  const doSend = async (formatted, text, translateMeta) => {
    const payload = {
      to: formatted,
      body: text,
      from: selectedNumber,
    };
    if (translateMeta) {
      payload.translate = true;
      payload.target_lang = translateMeta.targetLang;
      payload.source_lang = translateMeta.sourceLang;
      payload.domain = translateMeta.domain;
    }

    setSending(true);
    // An own send always lands the view on the new bubble, even if the user
    // had scrolled up to re-read something (set before the response appends).
    forceScrollRef.current = true;
    nearBottomRef.current = true;

    if (attachment) {
      try {
        const form = new FormData();
        form.append('file', attachment);
        const uploadRes = await fetch(`${API_BASE}/api/media/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: form,
        });
        const uploadData = await uploadRes.json().catch(() => ({}));
        if (!uploadRes.ok || !uploadData.url) {
          setError(uploadData.error || 'Attachment upload failed');
          setSending(false);
          return;
        }
        payload.media_url = [uploadData.url];
      } catch (e) {
        setError('Attachment upload failed');
        setSending(false);
        return;
      }
    }

    console.log('[SMS] sending:', JSON.stringify({ ...payload, body: payload.body?.slice(0, 40) }));

    try {
      const res = await fetch(`${API_BASE}/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}`, 'ngrok-skip-browser-warning': 'true' },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (parseErr) {
        console.error('[SMS] non-JSON response:', res.status, raw?.slice(0, 200));
        setError(
          res.status >= 500
            ? `SMS gateway error (${res.status}). Check SignalWire / server logs.`
            : `Bad SMS response (${res.status})`,
        );
        return;
      }
      console.log('[SMS] response:', res.status, {
        sid: data?.sid,
        error: data?.error,
        error_code: data?.error_code,
        error_message: data?.error_message,
        status: data?.status,
        from: data?.from,
        to: data?.to,
        body: data?.body,
        body_original: data?.body_original,
        body_translated: data?.body_translated,
        detected_lang: data?.detected_lang,
        delivery_failed: data?.delivery_failed,
      });

      // Keep the bubble even when delivery fails so the thread is not empty.
      if (data?.sid) {
        setMessages((prev) => {
          if (prev.some((m) => m.sid === data.sid)) return prev;
          return [...prev, data];
        });
        if (!activeContact || activeContact === '__new__') setActiveContact(formatted);
        setMsgText('');
        setAttachment(null);
        setAttachmentPreview(null);
        if (fileInputRef?.current) fileInputRef.current.value = '';
      }

      if (
        !res.ok
        || data.error
        || data.delivery_failed
        || data.status === 'failed'
        || data.status === 'undelivered'
      ) {
        const detail = data.error_message || data.error || `Delivery ${data.status || res.status}`;
        const usedFrom = data.from || selectedNumber;
        const mismatch =
          data.from && selectedNumber && data.from !== selectedNumber
            ? ` (server used ${data.from}, header showed ${selectedNumber})`
            : '';
        setError(`${detail} — from ${usedFrom}${mismatch}`);
      }
    } catch (err) {
      console.error('[SMS] error:', err);
      setError('Network error — is the backend running?');
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    setError('');
    if (translateConfirm) return; // confirm/cancel the pending dialog first
    if (!selectedNumber) { setError('Select a line from the header dropdown'); return; }
    const toNum = (activeContact && activeContact !== '__new__') ? activeContact : (toNumber || '').trim();
    const text = (msgText || '').trim();

    if (!toNum) { setError('Enter a phone number'); return; }
    if (!text && !attachment) { setError('Enter a message or attach a file'); return; }

    const formatted = formatNumber(toNum);
    const translateApplicable =
      !!text
      && (threadMode === 'always' || threadMode === 'ask')
      && translateSettings.userLang !== threadRemoteLang;

    if (!translateApplicable) {
      await doSend(formatted, text, null);
      return;
    }

    // Translate on demand, right here at send time — not from a background
    // debounced preview. The debounced-preview approach had a real race: a
    // short message typed and sent within ~450ms beat the debounce timer,
    // so composePreview was still empty and the message went out
    // untranslated with no indication anything had been skipped.
    setComposeTranslating(true);
    let translated = text;
    let targetLang = threadRemoteLang;
    try {
      const data = await translateTextApi(authToken, {
        text,
        sourceLang: translateSettings.userLang,
        targetLang: threadRemoteLang,
        domain: translateSettings.domain,
      });
      translated = data.translated || text;
      targetLang = data.target_lang || threadRemoteLang;
    } catch (e) {
      console.warn('[SMS-TRANSLATE] compose translate failed, sending original', e);
      setComposeTranslating(false);
      await doSend(formatted, text, null);
      return;
    }
    setComposeTranslating(false);

    const translateMeta = {
      targetLang,
      sourceLang: translateSettings.userLang,
      domain: translateSettings.domain,
    };

    if (threadMode === 'ask') {
      // "Ask" means ask — show the translated text and let the user
      // confirm or cancel before anything goes out.
      setTranslateConfirm({ formatted, text, translated, targetLang });
      return;
    }

    // "always": no prompt, send the translated text directly.
    await doSend(formatted, translated, translateMeta);
  };

  const confirmSendTranslated = async () => {
    if (!translateConfirm) return;
    const { formatted, translated, targetLang } = translateConfirm;
    setTranslateConfirm(null);
    await doSend(formatted, translated, {
      targetLang,
      sourceLang: translateSettings.userLang,
      domain: translateSettings.domain,
    });
  };

  const cancelTranslateConfirm = () => {
    setTranslateConfirm(null);
  };

  const renderTranslateBar = () => {
    if (!activeContact || activeContact === '__new__') return null;
    const langLabel = TRANSLATE_LANGUAGES[threadRemoteLang] || threadRemoteLang;
    const yourLabel = TRANSLATE_LANGUAGES[translateSettings.userLang] || translateSettings.userLang;
    return (
      <div
        data-testid="sms-translate-bar"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          marginBottom: 10,
          fontSize: 14,
          color: 'var(--truva-text-muted)',
        }}
      >
        <span style={{ fontWeight: 600, color: 'var(--truva-text-secondary, inherit)' }}>
          Live Translate
        </span>
        <button
          type="button"
          aria-label={threadMode !== 'off' ? 'Translate on, tap to turn off' : 'Translate off, tap to turn on'}
          data-testid="sms-translate-toggle"
          onClick={() => setThreadTranslateMode(threadMode !== 'off' ? 'off' : 'always')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            border: '1px solid var(--truva-border)',
            borderRadius: 999,
            padding: '3px 10px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            background: threadMode !== 'off' ? 'var(--truva-success-green)' : 'transparent',
            color: threadMode !== 'off' ? 'var(--truva-on-action)' : 'inherit',
          }}
        >
          🌐 {threadMode !== 'off' ? 'On' : 'Off'}
        </button>
        <span>
          {yourLabel} ↔ {langLabel}
        </span>
      </div>
    );
  };

  const renderBubbleBody = (m, inbound) => {
    const showingOriginal = !!showOriginal[m.sid];
    const hasTx = !!m.body_translated;
    const primary = inbound
      ? (showingOriginal || !hasTx ? m.body : m.body_translated)
      : (m.body_original && !showingOriginal ? m.body_original : m.body);
    const secondaryLabel = inbound
      ? (showingOriginal ? 'Show translation' : (hasTx ? 'Original' : null))
      : (m.body_original ? (showingOriginal ? 'Show what you typed' : 'Sent translation') : null);

    return (
      <>
        <div
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {linkifyText(primary)}
        </div>
        {m.media_url ? (
          <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {m.media_url.split(',').filter(Boolean).map((url, idx) => (
              <MediaAttachment key={idx} url={url} sid={m.sid} authToken={authToken} />
            ))}
          </div>
        ) : null}
        {m.body_original && !inbound && !showingOriginal ? (
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>
            Sent: {m.body}
          </div>
        ) : null}
        {secondaryLabel ? (
          <button
            type="button"
            data-testid={`sms-toggle-original-${m.sid}`}
            onClick={() => setShowOriginal((prev) => ({ ...prev, [m.sid]: !prev[m.sid] }))}
            style={{
              marginTop: 4,
              border: 'none',
              background: 'none',
              padding: 0,
              fontSize: 13,
              cursor: 'pointer',
              color: 'inherit',
              opacity: 0.85,
              textDecoration: 'underline',
            }}
          >
            {secondaryLabel}
          </button>
        ) : null}
        {inbound && !hasTx && threadMode === 'ask' && !translatingSids[m.sid] ? (
          <button
            type="button"
            data-testid={`sms-translate-msg-${m.sid}`}
            onClick={() => translateInbound(m)}
            style={{
              marginTop: 4,
              border: 'none',
              background: 'none',
              padding: 0,
              fontSize: 13,
              cursor: 'pointer',
              color: 'inherit',
              opacity: 0.9,
              textDecoration: 'underline',
            }}
          >
            Translate
          </button>
        ) : null}
        {translatingSids[m.sid] ? (
          <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>Translating…</div>
        ) : null}
      </>
    );
  };

  const renderCompose = () => (
    <div className="truva-glass-primary compose-pane ios-msg-compose" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setActiveContact(null)}
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0 }}>
          ←
        </button>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>New Message</h3>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ fontWeight: 600, fontSize: 14 }}>To:</label>
        <input type="tel" placeholder="Phone number (e.g. 7145551234)"
          value={toNumber} onChange={(e) => setToNumber(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--truva-border)', fontSize: 14 }}
        />
        <ContactPicker
          selectedNumber={selectedNumber}
          authToken={authToken}
          onSelect={(num) => setToNumber(num)}
        />
      </div>
      {renderMessageInput()}
    </div>
  );

  const renderMessageInput = () => (
    <div className="ios-msg-composer" style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {error && (
        <div style={{ color: 'var(--truva-danger-red)', background: 'var(--truva-danger-tint)', padding: '6px 10px', borderRadius: 6, fontSize: 15 }}>
          {error}
        </div>
      )}

      {selectedNumber ? (
        <div
          data-testid="sms-from-line"
          style={{ fontSize: 14, color: 'var(--truva-text-secondary)', padding: '0 2px' }}
        >
          From {selectedNumber}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: 'var(--truva-danger-red)', padding: '0 2px' }}>
          Select a line in the header to send
        </div>
      )}

      {attachmentPreview && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {attachment && attachment.type && attachment.type.startsWith('image/') ? (
            <img src={attachmentPreview} alt="attachment preview" style={{ maxWidth: 120, borderRadius: 8 }} />
          ) : (
            <div style={{ padding: 8, borderRadius: 8, background: 'var(--truva-surface-hover)' }}>
              {attachment?.name || 'Attachment'}
            </div>
          )}
          <button onClick={() => { setAttachment(null); setAttachmentPreview(null); if (fileInputRef?.current) fileInputRef.current.value = ''; }} style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'var(--truva-surface-2)', color: 'var(--truva-text-secondary)', fontSize: 16, lineHeight: 1, flexShrink: 0, cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {translateConfirm ? (
        <div
          data-testid="sms-translate-confirm"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--truva-border)',
            background: 'var(--truva-surface-hover)',
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            Send in {TRANSLATE_LANGUAGES[translateConfirm.targetLang] || translateConfirm.targetLang}?
          </div>
          <div style={{ fontSize: 15 }}>{translateConfirm.translated}</div>
          <div style={{ fontSize: 13, color: 'var(--truva-text-muted)' }}>
            Your text: {translateConfirm.text}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              data-testid="sms-translate-confirm-cancel"
              onClick={cancelTranslateConfirm}
              disabled={sending}
              style={{ borderRadius: 8, padding: '8px 12px', fontSize: 15, border: '1px solid var(--truva-border)', background: 'none', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="sms-translate-confirm-send"
              onClick={confirmSendTranslated}
              disabled={sending}
              className="btn-primary bg-truva-action-blue"
              style={{ borderRadius: 8, padding: '8px 12px', fontSize: 15, fontWeight: 600, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.5 : 1 }}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
        </div>
      ) : null}

      {composeTranslating ? (
        <div
          data-testid="sms-compose-preview"
          style={{
            fontSize: 14,
            color: 'var(--truva-text-muted)',
            padding: '6px 10px',
            borderRadius: 8,
            border: '1px dashed var(--truva-border)',
          }}
        >
          Translating…
        </div>
      ) : null}

      <div className="ios-msg-composer-row" style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) { setAttachment(f); const url = f.type?.startsWith('image/') ? URL.createObjectURL(f) : f.name; setAttachmentPreview(url); } }} />
        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!!translateConfirm} style={{ width: 36, height: 36, borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--truva-surface-2)', border: 'none', color: 'var(--truva-text-secondary)', fontSize: 18, lineHeight: 1, flexShrink: 0, cursor: translateConfirm ? 'not-allowed' : 'pointer' }} aria-label="Attach">📎</button>
        <textarea rows={2} placeholder="Type your message…"
          value={msgText} onChange={(e) => setMsgText(e.target.value)}
          disabled={!!translateConfirm}
          onKeyDown={(e) => {
            // Enter = newline (SMS / iMessage-style). Ctrl/Cmd+Enter = send.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSend();
            }
          }}
          style={{
            flex: 1, padding: '10px 14px', borderRadius: 'var(--truva-radius-pill)', border: 'var(--truva-hairline) solid var(--truva-border)',
            background: 'var(--truva-surface-2)', color: 'var(--truva-text)',
            fontSize: 'var(--truva-type-subhead)', fontFamily: 'inherit', resize: 'none'
          }}
        />
        <button onClick={handleSend} disabled={sending || composeTranslating || !!translateConfirm} className="btn-primary bg-truva-action-blue ios-msg-send"
          style={{
            width: 'auto', height: 32, minWidth: 56, borderRadius: 'var(--truva-radius-pill)', flexShrink: 0,
            padding: '0 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--truva-action-blue)', color: 'var(--truva-on-action)',
            fontSize: 'var(--truva-type-subhead)', fontWeight: 600,
            cursor: (sending || composeTranslating || translateConfirm) ? 'not-allowed' : 'pointer', opacity: (sending || composeTranslating || translateConfirm) ? 0.5 : 1,
          }}>
          {composeTranslating ? '…' : (sending ? '…' : 'Send')}
        </button>
      </div>
    </div>
  );

  const renderThread = () => (
    <div className="truva-glass-primary thread-pane ios-msg-thread" style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <button onClick={() => { setActiveContact(null); setError(''); }}
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0 }}>
          ←
        </button>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{savedContactName(savedContacts, activeContact) || displayNumber(activeContact)}</h3>
        {typeof handleCall === 'function' && (
          <button
            title="Call"
            aria-label={`Call ${displayNumber(activeContact)}`}
            onClick={() => { const formatted = formatNumber(activeContact || ''); if (typeof setNumber === 'function') setNumber(formatted); if (setTab) setTab('phone'); handleCall(formatted); }}
            style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 0 }}
          >📞</button>
        )}
        {typeof setPrefillContactNumber === 'function' && (isNumberSaved(savedContacts, activeContact) ? (
          <AddToExistingContact
            selectedNumber={selectedNumber}
            authToken={authToken}
            number={activeContact}
            contacts={savedContacts}
            onAdded={reloadSavedContacts}
          />
        ) : (
          <button
            title="Add to Contacts"
            aria-label={`Add ${displayNumber(activeContact)} to Contacts`}
            onClick={() => { setPrefillContactNumber(activeContact); if (setTab) setTab('contacts'); }}
            style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 0 }}
          >➕</button>
        ))}
      </div>

      {renderTranslateBar()}

      <div
        className="ios-msg-thread-scroll"
        ref={threadScrollRef}
        onScroll={handleThreadScroll}
        style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}
      >
        {threadMessages.length === 0 && (
          <p style={{ color: 'var(--truva-text-muted)', fontSize: 15, textAlign: 'center', marginTop: 40 }}>No messages yet</p>
        )}
        {threadMessages.map((m, i) => {
          const inbound = m.direction !== 'outbound';
          const bubbleStyle = {
            padding: '8px 12px', borderRadius: 18, fontSize: 'var(--truva-type-subhead)', maxWidth: '75%',
            lineHeight: 1.35,
            alignSelf: inbound ? 'flex-start' : 'flex-end',
            color: inbound ? 'var(--truva-bubble-received-text)' : 'var(--truva-on-action)',
            ...(inbound
              ? { background: 'var(--truva-bubble-received-bg)' }
              : { background: 'var(--truva-action-blue)' }),
          };
          return (
            <div key={m.sid || i} className={inbound ? 'truva-glass-secondary msg-bubble' : 'msg-bubble'} style={bubbleStyle}>
              {renderBubbleBody(m, inbound)}
              <div style={{ fontSize: 'var(--truva-type-caption)', opacity: 0.75, marginTop: 2, textAlign: 'center', color: inbound ? 'var(--truva-text-secondary)' : 'var(--truva-on-action)' }}>
                {m.timestamp ? formatInTimeZone(m.timestamp, displayTz, 'time') : ''}
                {!inbound && (m.status === 'failed' || m.status === 'undelivered') ? ' · Not delivered' : ''}
              </div>
            </div>
          );
        })}
      </div>

      {showNewPill ? (
        <button
          type="button"
          data-testid="sms-new-message-pill"
          onClick={scrollToNewest}
          className="new-message-pill"
          style={{
            alignSelf: 'center',
            marginBottom: 8,
            padding: '4px 12px',
            borderRadius: 999,
            border: '1px solid var(--truva-border)',
            background: 'var(--truva-surface-2)',
            color: 'var(--truva-action-blue)',
            fontSize: 'var(--truva-type-footnote)',
            fontWeight: 600,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          New message ↓
        </button>
      ) : null}

      {renderMessageInput()}
    </div>
  );

  const renderConversationList = () => (
    <div className="truva-glass-secondary conversation-pane ios-msg-list" ref={ptr.ref} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PullIndicator pull={ptr.pull} refreshing={ptr.refreshing} threshold={ptr.threshold} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Messages</h3>
        <button onClick={() => { setActiveContact('__new__'); setToNumber(''); setMsgText(''); setError(''); }} className="btn-primary" style={{ borderRadius: 8, padding: '6px 14px', fontSize: 15, fontWeight: 600 }}>
          + New
        </button>
      </div>

      <div className="ios-msg-list-scroll" style={{ flex: 1, overflowY: 'auto' }}>
        {conversations.length === 0 && !messagesLoaded && (
          <p style={{ color: 'var(--truva-text-muted)', fontSize: 15, textAlign: 'center', marginTop: 40 }}>Loading…</p>
        )}
        {conversations.length === 0 && messagesLoaded && (
          <p style={{ color: 'var(--truva-text-muted)', fontSize: 15, textAlign: 'center', marginTop: 40 }}>
            No conversations yet. Tap "+ New" to send a message.
          </p>
        )}
        {conversations.map((conv) => (
          <div key={conv.contact}
            onClick={() => { setActiveContact(conv.contact); setMsgText(''); setError(''); }}
            className={conv.unread ? 'conversation-item ios-msg-row is-unread' : 'conversation-item ios-msg-row'}
            style={{
              display: 'flex', flexDirection: 'column', gap: 2,
              background: 'var(--truva-grouped-row-bg)',
              minHeight: 'calc(var(--truva-list-row-min-h) + 16px)',
              borderBottom: 'none',
              cursor: 'pointer', borderRadius: 0,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 'var(--truva-weight-medium)', fontSize: 'var(--truva-type-body)', color: 'var(--truva-text)' }}>{savedContactName(savedContacts, conv.contact) || displayNumber(conv.contact)}</span>
              <span style={{ fontSize: 'var(--truva-type-footnote)', color: 'var(--truva-text-secondary)', textAlign: 'right', whiteSpace: 'nowrap', marginLeft: 8 }}>
                {conv.lastMessage?.timestamp
                  ? formatInTimeZone(conv.lastMessage.timestamp, displayTz, 'date')
                  : ''}
              </span>
            </div>
            <div style={{
              fontSize: 'var(--truva-type-subhead)', color: 'var(--truva-text-secondary)', overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%'
            }}>
              {conv.lastMessage?.direction === 'outbound' ? 'You: ' : ''}
              {conv.lastMessage?.body_translated || conv.lastMessage?.body_original || conv.lastMessage?.body || ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (activeContact === '__new__') return renderCompose();
  if (activeContact) return renderThread();
  return renderConversationList();
}
